/**
 * A child's page, worked out from what the parent already holds.
 *
 * "Let me see what Ravi sees" is a question a parent should not have to pick
 * up a tablet to answer, and until now it needed one: the child's view is
 * built by `/v1/child/state` from a device token this browser does not have
 * and should not want.
 *
 * NOTHING NEW IS FETCHED. Every figure below is already in the `FamilyState`
 * the parent decrypted to draw Home -- the child's balance, the goals they
 * are named on, their share of each entry. So this is a pure function over
 * state, which is why it can be tested properly and why it adds no route, no
 * permission and no way for a parent to see anything they could not see by
 * scrolling.
 *
 * WHY IT IS A SEPARATE FUNCTION rather than a component reading state inline:
 * every line here has to produce the SAME NUMBER the child is looking at, and
 * a number that only nearly matches is worse than no feature at all. A parent
 * who reads $40 while their child reads $38 will trust the wrong one. So the
 * arithmetic is lifted out where a test can hold it against the server's own
 * rules, and the three that matter are pinned in childView.test.ts:
 *
 *   balance   held[pid]                      -- NOT held + shared. The shared
 *                                               pot is family money; a child's
 *                                               page has never shown it.
 *   goal mine funds[pid]                     -- what they put in, as child.py
 *   entries   the rows with a share for them, at their share
 *
 * If `handlers/child.py` ever changes one of those, the test here should fail
 * and this comment is the reason to come looking.
 */

import { isShowing, type Entry, type FamilyState, type Person } from './state';
import { localToday } from './goals';

export interface ChildViewGoal {
  id: string;
  title: string;
  target: number;
  icon: string;
  status: string;
  /** What this child has put in. Never the goal's whole pot. */
  mine: number;
  /** Everything in the goal, as the child's page shows it. */
  pot: number;
}

export interface ChildViewEntry {
  id: string;
  date: string;
  /** THIS child's share of the entry, in integer cents. */
  amount: number;
  kind: string;
  voided: boolean;
  reason: string;
}

export interface ChildView {
  personId: string;
  name: string;
  /** Their approved character -- the one their own page shows. */
  avatar: Person['avatar'];
  balance: number;
  goals: ChildViewGoal[];
  entries: ChildViewEntry[];
}

/**
 * What one child's page shows, from the family state a parent has open.
 *
 * Returns null for a person who is not in the roster, rather than an empty
 * view: "this child has nothing" and "there is no such child" are different
 * answers and the screen should not draw the first when it means the second.
 */
export function childView(state: FamilyState, personId: string): ChildView | null {
  const person: Person | undefined = state.people.find((one) => one.id === personId);
  if (!person) return null;

  return {
    personId,
    name: person.name,
    avatar: person.avatar,
    // `held`, not `held + shared`. See the note above: the shared pot belongs
    // to the family, and the child's own page has never counted it.
    balance: person.held,
    goals: state.goals
      // As child.py: theirs, or everybody's; active, or finished and left up.
      .filter(
        (goal) =>
          (goal.people.length === 0 || goal.people.includes(personId) || personId in goal.funds) &&
          (goal.status === 'active' || isShowing(goal, localToday())),
      )
      .map((goal) => ({
        id: goal.id,
        title: goal.title,
        target: goal.target,
        icon: goal.icon,
        status: goal.status,
        mine: goal.funds[personId] ?? 0,
        pot: goal.pot,
      })),
    entries: state.entries
      // `shares` is keyed by person id and only carries the people an entry
      // was split between, so membership of the map IS the filter. Checking
      // `people.includes` instead would keep rows with no share in them.
      .filter((entry) => Object.prototype.hasOwnProperty.call(entry.shares, personId))
      .map((entry) => oneEntry(entry, personId)),
  };
}

function oneEntry(entry: Entry, personId: string): ChildViewEntry {
  return {
    id: entry.id,
    date: entry.date,
    amount: entry.shares[personId] ?? 0,
    kind: entry.kind,
    voided: entry.voided,
    reason: entry.reason,
  };
}

/**
 * The children worth offering a page for.
 *
 * Everyone approved, in roster order. `pending` is deliberately included --
 * a person a parent has added but whose device has not linked yet still has
 * a page, and it is the page a parent most wants to look at before handing
 * over a tablet.
 */
export function viewableChildren(state: FamilyState): Person[] {
  return state.people.filter((person) => person.status !== 'removed');
}
